import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { connect, DataItem } from 'scrivito'

import { useAiDescriptionField } from './useAiDescriptionField'
import { CharacterCounter } from '@/Atomic/Atoms/CharacterCounter/CharacterCounter'
import { AiAttributionBadge } from '@/Atomic/Molecules/AiAttributionBadge'
import { Button } from '@/Atomic/Molecules/Button'
import { Textarea } from '@/Atomic/Molecules/Inputs/Textarea'
import { DropdownMenu } from '@/Atomic/Organisms/DropdownMenu'
import { FieldMap } from '@/utils/CRUD/read/dataItem/types'
import './AiDescriptionField.scss'

interface AiDescriptionFieldProps {
  dataItem: DataItem
  fieldMap: FieldMap
}

/**
 * AI-assisted description field for List/Segment items (CRM-1567). Combines
 * manual writing with AI Generate/Review actions behind a single Sparkles
 * icon, per the CRM-1327 implementation notes.
 */
export const AiDescriptionField: React.FC<AiDescriptionFieldProps> = connect(
  ({ dataItem, fieldMap }) => {
    const { t } = useTranslation()
    const {
      storedDescription,
      isReadonly,
      characterLimit,
      isEditing,
      draftText,
      setDraftText,
      activeProposal,
      isGenerating,
      action,
      startEditing,
      handleSparkleClick,
      handleGenerate,
      handleReview,
      handleConfirm,
      handleCancel,
    } = useAiDescriptionField(dataItem, fieldMap)

    const sparkleTooltip =
      action === 'generate'
        ? t('list.generate')
        : action === 'review'
          ? t('list.review')
          : action === 'menu'
            ? t('list.aiActions')
            : ''

    const sparkleTrigger = !isReadonly && (
      <DropdownMenu
        menuItems={[
          { label: t('list.generate'), onClick: handleGenerate },
          { label: t('list.review'), onClick: handleReview },
        ]}
        triggerIcon="sparkle"
        triggerIconColor="ai"
        triggerTooltip={sparkleTooltip}
        disabled={action === 'disabled' || isGenerating}
        onBeforeToggle={handleSparkleClick}
        className="ai-description-field-sparkle"
      />
    )

    if (!isEditing) {
      const onKeyDown: React.KeyboardEventHandler = (e) => {
        if (isReadonly) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          startEditing()
        }
      }

      return (
        <div
          className={classNames('ai-description-field-display', {
            empty: !storedDescription,
          })}
          role={isReadonly ? undefined : 'button'}
          tabIndex={isReadonly ? undefined : 0}
          onClick={isReadonly ? undefined : startEditing}
          onKeyDown={onKeyDown}
        >
          <span className="ai-description-field-text">
            {storedDescription || t('list.writeDescription')}
          </span>
          {sparkleTrigger}
        </div>
      )
    }

    const draftLength = draftText.length

    return (
      <div className="ai-description-field-editing">
        <div className="ai-description-field-textarea-wrapper">
          <Textarea
            name={fieldMap.name}
            label=" "
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            disabled={isGenerating}
            className="ai-description-field-textarea"
            autoFocus
          />
          {sparkleTrigger}
        </div>
        <div className="ai-description-field-meta-row">
          <div className="ai-description-field-badge-slot">
            {activeProposal && (
              <AiAttributionBadge
                origin={activeProposal.origin}
                visible={draftText === activeProposal.text}
              />
            )}
          </div>
          {typeof characterLimit === 'number' && characterLimit > 0 && (
            <CharacterCounter
              count={draftLength}
              maxLength={characterLimit}
              isOverLimit={draftLength > characterLimit}
            />
          )}
        </div>
        <div className="buttons g-8">
          <Button
            variant="icon"
            icon="cross"
            className="edit-button"
            onClick={handleCancel}
            tooltip={t('tooltip.cancel')}
            disabled={isGenerating}
          />
          <Button
            variant="icon"
            icon="check"
            className="edit-button"
            onClick={handleConfirm}
            tooltip={t('tooltip.save')}
            disabled={isGenerating}
          />
        </div>
      </div>
    )
  },
)
